import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toolsApi } from '@/lib/api/tools.api';

import type {
  CreateCustomToolDto,
  UpdateCustomToolDto,
  CustomToolQuery,
  TestCustomToolDto,
} from '@repo/shared-types';

export function useTools() {
  return useQuery({
    queryKey: ['tools'],
    queryFn: () => toolsApi.getTools(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGeminiModels() {
  return useQuery({
    queryKey: ['gemini-models'],
    queryFn: () => toolsApi.getGeminiModels(),
    staleTime: 7 * 24 * 60 * 60 * 1000,
  });
}

// Custom tools hooks
export function useCustomTools(query?: CustomToolQuery) {
  return useQuery({
    queryKey: ['custom-tools', query],
    queryFn: () => toolsApi.getCustomTools(query),
  });
}

export function useCustomTool(id: string) {
  return useQuery({
    queryKey: ['custom-tools', id],
    queryFn: () => toolsApi.getCustomTool(id),
    enabled: !!id,
  });
}

export function useCreateCustomTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomToolDto) => toolsApi.createCustomTool(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-tools'] });
    },
  });
}

export function useUpdateCustomTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomToolDto }) =>
      toolsApi.updateCustomTool(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-tools'] });
    },
  });
}

export function useDeleteCustomTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      toolsApi.deleteCustomTool(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-tools'] });
      queryClient.invalidateQueries({ queryKey: ['configurations'] });
    },
  });
}

export function useCustomToolUsage(id: string) {
  return useQuery({
    queryKey: ['custom-tools', id, 'usage'],
    queryFn: () => toolsApi.getCustomToolUsage(id),
    enabled: !!id,
  });
}

export function useTestCustomTool() {
  return useMutation({
    mutationFn: (data: TestCustomToolDto) => toolsApi.testCustomTool(data),
  });
}
